import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";

/* Fırat brand CSS overrides — mirror of BitrixContactForm.IFRAME_BRAND_CSS.
   Kept local so this embed can be dropped into any landing page without
   depending on the multi-topic contact block. */
const IFRAME_BRAND_CSS = `
  html, body {
    margin:0; padding:0; background:transparent;
    height:auto !important; min-height:0 !important; overflow:hidden !important;
  }
  body {
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    color: #3A3A3A; padding: 4px; overflow-x: hidden;
  }
  .b24-form, .b24-form-wrapper, .b24-form-content, .b24-form-body,
  .b24-form-inner, .b24-window, .b24-window-scrollable,
  .b24-form-content > div, [class*="b24-form"] {
    height: auto !important; min-height: 0 !important; max-height: none !important;
  }
  .b24-form, .b24-form-wrapper, .b24-form-content,
  .b24-window-scrollable, .b24-form-body, .b24-form-inner {
    background: transparent !important; box-shadow: none !important;
    padding: 0 !important; margin: 0 !important; border: none !important;
    max-width: 100% !important;
  }
  .b24-form-header, .b24-form-header-title, .b24-form-header-description,
  .b24-form-footer-info, .b24-form-sign-block, .b24-form-sign,
  .b24-form-sign-content, .b24-form-sign-container, .b24-form-copyright,
  [class*="b24-form-sign"], [class*="b24-copyright"], [class*="b24-brand"],
  a[href*="bitrix24"], a[href*="bitrix"], .b24-widget-branding { display: none !important; }

  .b24-form-field, .b24-form-field-item { margin-bottom: 1.05rem !important; }
  .b24-form-control, input[type=text], input[type=email], input[type=tel],
  input[type=number], input[type=url], textarea, select {
    background: #fff !important; border: 1px solid rgba(0,0,0,0.15) !important;
    border-radius: 0 !important; padding: 0.85rem 1rem !important;
    font-family: 'Inter', sans-serif !important; font-size: 0.95rem !important;
    color: #3A3A3A !important; box-shadow: none !important;
    transition: border-color 300ms ease !important;
    width: 100% !important; box-sizing: border-box !important;
  }
  .b24-form-control:focus, input:focus, textarea:focus, select:focus {
    border-color: #22346B !important; outline: none !important; box-shadow: none !important;
  }
  textarea { min-height: 96px !important; resize: vertical !important; }

  .b24-form-control-label, label {
    font-family: 'Inter', sans-serif !important;
    font-size: 0.66rem !important; letter-spacing: 0.28em !important;
    text-transform: uppercase !important; color: #8B8D93 !important;
    font-weight: 500 !important; margin-bottom: 0.4rem !important; display: block !important;
  }
  .b24-form-check-label, .b24-form-agreement-content, .b24-form-agreement {
    font-size: 0.78rem !important; color: #8B8D93 !important; line-height: 1.5 !important;
    letter-spacing: 0 !important; text-transform: none !important; font-weight: 400 !important;
  }
  .b24-form-check-decor, .b24-form-checkbox-decor {
    border-color: #22346B !important; background: transparent !important;
  }
  .b24-form-check-decor.b24-form-check-decor-active,
  .b24-form-checkbox-decor.b24-form-checkbox-decor-active {
    background: #22346B !important; border-color: #22346B !important;
  }

  .b24-form-btn, button[type="submit"], .b24-form-btn-container button {
    background: #C9A227 !important; color: #0B1631 !important;
    border: 1px solid #C9A227 !important; border-radius: 0 !important;
    padding: 1.05rem 2.25rem !important; font-family: 'Inter', sans-serif !important;
    font-size: 0.74rem !important; letter-spacing: 0.28em !important;
    text-transform: uppercase !important; font-weight: 600 !important;
    transition: all 300ms ease !important; cursor: pointer !important;
    box-shadow: 0 8px 24px rgba(201,162,39,0.25) !important;
    width: 100% !important;
  }
  .b24-form-btn:hover, button[type="submit"]:hover {
    background: #d9b03a !important; border-color: #d9b03a !important;
    transform: translateY(-1px) !important; box-shadow: 0 12px 30px rgba(201,162,39,0.35) !important;
  }
  .b24-form-success-title { color: #1A2A5A !important; font-family: 'Cormorant Garamond', serif !important; }
  .b24-form-success-text { color: #8B8D93 !important; }
  .b24-form-control-alert, .b24-form-field-error { color: #b91c1c !important; font-size: 0.75rem !important; }
`;

const buildSrcDoc = (form, extraQuery = "", parentOrigin = "*") => {
  const cacheBuster = Math.floor(Date.now() / 180000);
  const sep = form.loader_url.includes("?") ? "&" : "?";
  const loader = `${form.loader_url}${sep}_=${cacheBuster}${extraQuery ? `&${extraQuery}` : ""}`;
  // Serialize the parent origin safely so it lands as a JS string literal in the iframe.
  const originLiteral = JSON.stringify(String(parentOrigin || "*"));
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<base target="_top" />
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=Inter:wght@300;400;500;600&display=swap" />
<style>${IFRAME_BRAND_CSS}</style>
</head>
<body>
<script data-b24-form="${form.form_id}" data-skip-moving="true"></script>
<script>
(function(w,d,u){
  var s=d.createElement('script');s.async=true;s.src=u;
  var h=d.getElementsByTagName('script')[0];h.parentNode.insertBefore(s,h);
})(window,document,'${loader}');
(function(){
  var lastH = 0;
  function scrub(){
    var sel='a[href*="bitrix24"], a[href*="bitrix"], .b24-widget-branding, [class*="b24-form-sign"], [class*="b24-copyright"], [class*="b24-brand"], .b24-form-sign-block, .b24-form-sign';
    document.querySelectorAll(sel).forEach(function(el){ el.style.display='none'; el.remove(); });
  }
  function measure(){
    var maxBottom = 0;
    var els = document.body.querySelectorAll('*');
    for (var i=0;i<els.length;i++){
      var el = els[i];
      if (!el.offsetParent && el !== document.body) continue;
      var r = el.getBoundingClientRect();
      if (r.bottom > maxBottom) maxBottom = r.bottom;
    }
    return Math.min(2400, Math.max(120, Math.ceil(maxBottom)));
  }
  function post(){
    scrub();
    var h = measure();
    if (Math.abs(h - lastH) < 6) return;
    lastH = h;
    parent.postMessage({ __firatBitrixHeight: h }, ${originLiteral});
  }
  var mo = new MutationObserver(post);
  mo.observe(document.body, { childList:true, subtree:true, attributes:true });
  window.addEventListener('load', post);
  setInterval(post, 1200);
})();
</script>
</body>
</html>`;
};

/**
 * Embeds a single Bitrix24 form (no topic tabs).
 * Pass either the full `form` object or `formKey` (looks up from `forms[]`).
 */
const BitrixFormEmbed = ({ form, forms = [], formKey = "buy", extraQuery = "", testId = "bitrix-form-embed" }) => {
  const iframeRef = useRef(null);
  const [frameHeight, setFrameHeight] = useState(560);

  const active = useMemo(() => {
    if (form && form.form_id && form.loader_url) return form;
    return (forms || []).find((f) => f?.key === formKey && f?.form_id && f?.loader_url) || null;
  }, [form, forms, formKey]);

  useEffect(() => {
    const onMsg = (e) => {
      // Verify origin — the iframe is `srcDoc` so its origin is "null"; accept
      // only that, plus our own origin as a fallback for any same-origin dev tools.
      if (e.origin !== "null" && e.origin !== window.location.origin) return;
      // Verify the source is actually our iframe, not any other window
      if (iframeRef.current && e.source !== iframeRef.current.contentWindow) return;
      const h = e?.data?.__firatBitrixHeight;
      if (typeof h !== "number" || h <= 100 || h >= 2400) return;
      setFrameHeight((prev) => {
        const target = Math.min(2400, h + 8);
        if (Math.abs(target - prev) < 8) return prev;
        return target;
      });
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  useEffect(() => { setFrameHeight(560); }, [active?.form_id]);

  const srcDoc = useMemo(() => (
    active ? buildSrcDoc(active, extraQuery, typeof window !== "undefined" ? window.location.origin : "*") : ""
  ), [active, extraQuery]);

  if (!active) {
    return (
      <div className="w-full border border-black/10 p-6 md:p-8 bg-brand-light text-sm text-brand-medium" data-testid={`${testId}-missing`}>
        Form yapılandırması bulunamadı ({formKey}). Site Ayarları → Bitrix24 Formları bölümünden ekleyin.
      </div>
    );
  }

  return (
    <div className="w-full" data-testid={testId}>
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <iframe
          ref={iframeRef}
          title={`bitrix-${active.key || "form"}`}
          srcDoc={srcDoc}
          className="w-full block bg-transparent"
          style={{ height: frameHeight, border: "0" }}
          sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-top-navigation-by-user-activation"
          data-testid={`${testId}-iframe`}
        />
      </motion.div>
    </div>
  );
};

export default BitrixFormEmbed;
